/**
 * Detects failures to load front-end assets (JS/CSS chunks) and surfaces a single, non-intrusive toast asking the user to refresh.
 *
 * Why this exists: after a redeploy, content-hashed asset filenames change. A browser still holding a stale `index.html` (or a stale lazy-chunk reference from a long-lived session) will request a hash that no longer exists on the server and get a 404, breaking the page. 
 * The backend's no-cache header on index.html prevents this on a fresh navigation; this watcher is the runtime safety net for sessions that were already open across a deploy.
 *
 * We deliberately do NOT force a reload — a hard reload loop is possible if the asset is genuinely missing (not just stale), so we let the user decide.
 */

import { publishToast } from "./toastBus";

// Show the toast at most once per page lifetime — a single broken deploy can emit many asset errors and we don't want to spam the user.
let alreadyWarned = false;

const REFRESH_MESSAGE =
  "A new version of the app is available. Please refresh the page to continue.";

const warnOnce = (): void => {
  if (alreadyWarned) return;
  alreadyWarned = true;
  publishToast({ type: "warning", message: REFRESH_MESSAGE, duration: 30000 });
};

/**
 * Is this a resource (script/stylesheet) load failure rather than a normal runtime error? Resource errors fire on the element, don't bubble, and carry no `error` object — hence the capture-phase listener below.
 */
const isAssetLoadError = (event: Event): boolean => {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName;
  if (tag === "SCRIPT") return true;
  if (
    tag === "LINK" &&
    (target as HTMLLinkElement).rel === "stylesheet"
  ) {
    return true;
  }
  return false;
};

/**
 * Dynamic `import()` failures (lazy chunks) surface as unhandled promise rejections with a recognizable message rather than as resource error events.
 */
const isChunkLoadRejection = (reason: unknown): boolean => {
  const message =
    typeof reason === "string"
      ? reason
      : reason instanceof Error
      ? reason.message
      : "";
  return /Loading chunk \d+ failed|Loading CSS chunk|ChunkLoadError|dynamically imported module/i.test(
    message
  );
};

let installed = false;

export const installAssetErrorWatcher = (): void => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Capture phase: resource load errors do not bubble, so a listener on `window` only sees them when capture is enabled.
  window.addEventListener(
    "error",
    (event) => {
      if (isAssetLoadError(event)) warnOnce();
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadRejection(event.reason)) warnOnce();
  });
};
